import React, { createContext, useState, useContext } from 'react';
import { authService } from '@/services/api';
import { isAdmin, isEmployee, isTeamLeader, normalizeRole } from '@/utils/roleUtils';
import { applyAccessTokenClaims } from '@/shared/lib/teamLeaderAccess';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // استعادة بيانات المستخدم من localStorage عند التحميل
    try {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      if (savedUser && token) {
        return applyAccessTokenClaims(JSON.parse(savedUser), token);
      }
    } catch (error) {
      console.error('خطأ في استعادة بيانات المستخدم:', error);
    }
    return null;
  });
  const [loading] = useState(false);
  const [error, setError] = useState(null);

  const login = async (identifier, password) => {
    try {
      setError(null);
      const response = await authService.login({ identifier, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);
      
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تسجيل الدخول');
      throw err;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await authService.register(userData);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء التسجيل');
      throw err;
    }
  };

  const logout = async () => {
    try {
      // authService.logout() already handles API call and localStorage clearing
      const response = await authService.logout();
      
      // Log success message if available
      if (response?.success && response?.message) {
        console.log('✅', response.message);
      }
      
      // Clear user state (authService already cleared localStorage)
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
      // Even if API call fails, clear user state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      const response = await authService.forgotPassword(email);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء إرسال رابط إعادة تعيين كلمة المرور');
      throw err;
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      setError(null);
      const response = await authService.resetPassword(token, newPassword);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء إعادة تعيين كلمة المرور');
      throw err;
    }
  };

  const normalizedRole = normalizeRole(user?.role);
  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: isAdmin(normalizedRole),
    isTeamLeader: isTeamLeader(normalizedRole),
    isManager: user?.role === 'manager',
    isEmployee: isEmployee(normalizedRole),
    isViewer: user?.role === 'viewer'
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 